import { PriceSetSchema } from "@redotech/redo-common-model-money-common/price-set";
import { dropoffProviderType } from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import { BundleDataSchema } from "@redotech/redo-return-settings-primitives-common/bundle-data";
import { ExtendedWarrantySchema } from "@redotech/redo-return-warranties-common/extended-warranty";
import { InventoryPolicy } from "@redotech/redo-shopify-order-common/shopify-inventory-policy";
import { OriginAddressSchema } from "@redotech/redo-shopify-order-common/shopify-order";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { ExchangeGroup } from "../exchange-group";
import { FieldId, InputPartType } from "../return-flow-answers";
import { DateFromDateOrIsoStringSchema } from "./date-schema";

/**
 * A sensitive PP+ claim upload. `url` is nullable so the file can be deleted for
 * privacy after retention while the original `fileName` is kept for reference.
 */
export const ClaimDocumentSchema = z.object({
  fileName: z.string(),
  url: z.string().nullable(),
});
export type ClaimDocument = z.infer<typeof ClaimDocumentSchema>;

/**
 * Claim-attachment types allowed at upload. webp is allowed because we transcode
 * it to jpeg before sending to Shipsurance (which rejects webp); heic/video stay blocked.
 */
export const CLAIM_DOCUMENT_ACCEPTED_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "pdf",
  "docx",
] as const;

export const CLAIM_DOCUMENT_ACCEPTED_MIME_TYPES = [
  "image/jpg",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
] as const;

const CLAIM_DOCUMENT_CONTENT_TYPES: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

export function claimDocumentExtension(fileName: string): string {
  return fileName.split(".").pop()?.toLowerCase() ?? "";
}

export function isAcceptedClaimDocumentFileName(fileName: string): boolean {
  return (CLAIM_DOCUMENT_ACCEPTED_EXTENSIONS as readonly string[]).includes(
    claimDocumentExtension(fileName),
  );
}

export function claimDocumentContentType(fileName: string): string {
  return (
    CLAIM_DOCUMENT_CONTENT_TYPES[claimDocumentExtension(fileName)] ??
    "application/octet-stream"
  );
}

export interface ClaimDocumentAttachment {
  url: string;
  contentType: string;
  fileName: string;
}

/**
 * Collect a return's claim documents into Shipsurance-attachable files, dropping
 * deleted (null-url) uploads and file types Shipsurance rejects.
 */
export function collectClaimDocumentAttachments(
  documentGroups: (ClaimDocument[] | null | undefined)[],
): ClaimDocumentAttachment[] {
  return documentGroups
    .flatMap((group) => group ?? [])
    .filter(
      (document): document is ClaimDocument & { url: string } =>
        !!document.url && isAcceptedClaimDocumentFileName(document.fileName),
    )
    .map((document) => ({
      url: document.url,
      contentType: claimDocumentContentType(document.fileName),
      fileName: document.fileName,
    }));
}

export const exchangeVariantSchema_ = z.object({
  id: z.string(),
  sku: z.string().nullish(),
  productId: z.string().nullish(),
  productTitle: z.string().nullish(),
  title: z.string(),
  displayName: z.string(),
  price: z.string(),
  image: z.string().nullish(),
  inventoryQuantity: z.number(),
  inventoryTracked: z.boolean(),
  inventoryPolicy: createEnum(InventoryPolicy),
  selectedOptions: z.array(
    z.object({
      name: z.string(),
      value: z.string(),
      productAsVariant: z.boolean().nullish(),
    }),
  ),
  backorder: z.string().nullish(),
  metafields: z.record(z.string(), z.string()).nullish(),
});
export const exchangeVariantSchema: z.ZodType<ExchangeVariant> =
  exchangeVariantSchema_;
export interface ExchangeVariant extends z.infer<
  typeof exchangeVariantSchema_
> {}

export const NewExchangeItemDiscountSchema = z.object({
  value: z.number(),
  valueType: createEnum(["PERCENTAGE", "FIXED_AMOUNT"]),
});
export type NewExchangeItemDiscount = z.infer<
  typeof NewExchangeItemDiscountSchema
>;

export const NewExchangeItemProductOptionSchema = z.object({
  optionId: z.string(),
  value: z.string(),
  displayName: z.string().nullish(),
  displayValue: z.string().nullish(),
  optionType: z.string().nullish(),
});
export type NewExchangeItemProductOption = z.infer<
  typeof NewExchangeItemProductOptionSchema
>;

const AdvancedExchangeItemSchema_ = z.object({
  variantId: z.string(),
  productId: z.string(),
  title: z.string(),
  variantTitle: z.string(),
  /** @deprecated use priceSet instead */
  price: z.string(),
  priceSet: PriceSetSchema.nullish(),
  /** @deprecated use itemValueSet instead */
  itemValue: z.string().nullish(),
  itemValueSet: PriceSetSchema.nullish(),
  quantity: z.number(),
  images: z.array(z.string()),
  currencyCode: z.string(),
  attributes: z.record(z.string(), z.any()).nullish(),
  tax: z.string().nullish(),
  appliedDiscount: NewExchangeItemDiscountSchema.nullish(),
  productOptions: z.array(NewExchangeItemProductOptionSchema).nullish(),
  portalDisplayMetafieldValues: z.array(z.string()).nullish(),
});
export const AdvancedExchangeItemSchema: z.ZodType<AdvancedExchangeItem> =
  AdvancedExchangeItemSchema_;
export type AdvancedExchangeItem = z.infer<typeof AdvancedExchangeItemSchema_>;

export const NewExchangeItemSchema = z.object({
  variantId: z.string(),
  productId: z.string().nullish(),
  quantity: z.number(),
  properties: z.record(z.string(), z.any()).nullish(),
  appliedDiscount: NewExchangeItemDiscountSchema.nullish(),
  /** Storefront-cart modifier selections for shop-on-site exchanges. List types
   * drive BigCommerce re-pricing; others carry over for fulfillment only. */
  productOptions: z.array(NewExchangeItemProductOptionSchema).nullish(),
});

export type NewExchangeItem = z.infer<typeof NewExchangeItemSchema>;

const baseExchangeGroupSchema = z.object({
  type: createEnum([
    "tag:tag",
    "tag:collection",
    "name:collection",
    "collection:collection",
  ]),
  groupName: z.string(),
  priceDifference: createEnum(["ignore", "apply"]),
});

const collectionInfoSchema_ = z.object({
  id: z.string(),
  name: z.string(),
  kind: createEnum(["smart", "custom"]),
});
const collectionInfoSchema: z.ZodType<CollectionInfo> = collectionInfoSchema_;
export interface CollectionInfo extends z.infer<typeof collectionInfoSchema_> {}

export const exchangeGroupSchema = z.union([
  baseExchangeGroupSchema.extend({
    type: z.literal("tag:tag"),
    sourceTags: z.array(z.string()),
    targetTags: z.array(z.string()),
  }),
  baseExchangeGroupSchema.extend({
    type: z.literal("tag:collection"),
    sourceTags: z.array(z.string()),
    targetCollection: collectionInfoSchema,
  }),
  baseExchangeGroupSchema.extend({
    type: z.literal("name:collection"),
    sourceName: z.string(),
    targetCollection: collectionInfoSchema,
  }),
  baseExchangeGroupSchema.extend({
    type: z.literal("collection:collection"),
    sourceCollection: collectionInfoSchema,
    targetCollection: collectionInfoSchema,
  }),
]);

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const _: z.infer<typeof exchangeGroupSchema> = {} as ExchangeGroup;

const ResetSchema = z.object({
  step: z.number(),
  /** Full flow snapshot; legacy — new resets carry flowId/flowType instead. */
  returnFlow: z.any().nullish(),
  flowId: z.string().nullish(),
  flowType: z.string().nullish(),
});

const OrderTimeProductVariantMetafieldsSchema = z.record(
  z.string(),
  z.string(),
);

const discountNameSchema_ = z.object({ title: z.string(), code: z.string() });
export const discountNameSchema: z.ZodType<DiscountName> = discountNameSchema_;
export interface DiscountName extends z.infer<typeof discountNameSchema_> {}

export const ReturnProductSchema_ = z.object({
  id: z.string(),
  title: z.string(),
  variantTitle: z.string(),
  vendor: z.string().nullish(),
  variantId: z.string(),
  reset: ResetSchema.nullish(),
  /** @deprecated use images instead */
  image: z.string(),
  /**
   * This includes all images for the product, including variant images.
   * If it's not defined, use image as fallback. It will always be defined for Shopify.
   */
  images: z
    .array(z.object({ url: z.string(), alt: z.string().nullish() }))
    .nullish(),
  price: z.string(),
  percentOfBundle: z.number().nullish(),
  bundleData: BundleDataSchema.nullish(),
  tax: z.string(),
  currency: z.string(),
  productTags: z.array(z.string()),
  returnValue: z.string(),
  // The return value of returnable items can change depending on which products
  // are selected (e.g. if the bogo redistribution feature is enabled),
  // the pre-select return value is calculated before any products are selected
  preSelectedReturnValue: z.string().nullish(),
  bogoDiscountId: z.string().nullish(),
  sku: z.string(),
  deliveredOn: DateFromDateOrIsoStringSchema,
  /**
   * Carrier-reported delivered/picked-up, unlike `deliveredOn` which assumes
   * delivery 10 days post-fulfillment when there is no carrier tracker.
   */
  isDelivered: z.boolean().nullish(),
  fulfilledOn: DateFromDateOrIsoStringSchema,
  grams: z.number(),
  requiresShipping: z.boolean().nullish(),
  lastFulfillmentUpdateOn: DateFromDateOrIsoStringSchema,
  shipmentLocationId: z.coerce.string().nullish(),
  productId: z.string(),
  collections: z.array(z.string()),
  collectionIds: z.array(z.string()).nullish(),
  orderTimeCollections: z.array(z.string()).nullish(),
  productDiscounts: z.array(discountNameSchema),
  productDiscountedAmount: z.number(),
  loyaltyAmount: z.number().nullish(),
  productProperties: z.record(z.string(), z.any().nullable()).nullish(),
  /** Shopify native-bundle group id shared by a bundle's component line items. */
  lineItemGroupId: z.string().nullish(),
  productType: z.string(),
  productVariantMetafields: z.record(z.string(), z.any()).nullish(),
  /** Values selected by the merchant for display in the customer portal. */
  portalDisplayMetafieldValues: z.array(z.string()).nullish(),
  /** Live product tags fetched at return-draft time (for TIME_OF_RETURN product-tag conditions). */
  liveProductTags: z.array(z.string()).nullish(),
  /** Variant metafields snapshotted at order time (for TIME_OF_ORDER variant-metafield conditions). */
  orderTimeProductVariantMetafields:
    OrderTimeProductVariantMetafieldsSchema.nullish(),
  /** Variant compare-at price snapshotted at order time (for the compare-at-price condition). */
  compareAtPrice: z.number().nullish(),
  productMetafields: z.record(z.string(), z.any()).nullish(),
  variantData: z.object({
    productTitle: z.string(),
    status: z.string().nullish(),
    tags: z.array(z.string()),
    options: z.array(
      z.object({
        position: z.number(),
        name: z.string(),
        values: z.array(z.string()),
      }),
    ),
    variants: z.array(exchangeVariantSchema),
    /** Withheld by the unpublished filter; only same-item warranty replacement may use them. */
    unpublishedVariants: z.array(exchangeVariantSchema).nullish(),
    productMetafields: z.record(z.string(), z.string()).nullish(),
  }),
  fulfillmentOrigin: OriginAddressSchema.nullish(),
  isUnbundled: z.boolean().nullish(),
  isExpandedPrimaryProduct: z.boolean().nullish(),
  originalPrice: z.string().nullish(),
  taxRate: z.number().nullish(),
  quantity: z.number().nullish(),
  extendedWarranty: ExtendedWarrantySchema.nullish(),
  parentVariantId: z.string().nullish(),
  isCustomItem: z.boolean().nullish(),
});
export const ReturnProductSchema: z.ZodType<ReturnProduct> =
  ReturnProductSchema_;
export interface ReturnProduct extends z.infer<typeof ReturnProductSchema_> {}

const ReturnableItemSchema_ = z.object({
  id: z.string(),
  lineItemId: z.string().nullish(),
  primaryProductId: z.string(),
  value: z.string().nullish(),
  products: z.array(ReturnProductSchema),
  isExpanded: z.boolean(),
  rules: z.any().nullish(), // todo
  orderId: z.string(),
  selected: z.optional(z.boolean().nullish()),
  isTnplContractLineItem: z.optional(z.boolean().nullish()),
  parentReturnValue: z.string().nullish(),
});
export const ReturnableItemSchema: z.ZodType<ReturnableItem> =
  ReturnableItemSchema_;
export interface ReturnableItem extends z.infer<typeof ReturnableItemSchema_> {}

const NonReturnableItemSchema_ = z.object({
  lineItemId: z.string(),
  orderId: z.string().optional(),
  reasons: z.array(z.string()),
  title: z.string(),
  variantTitle: z.string().nullish(),
  image: z.string().nullish(),
  price: z.string(),
  /**
   * Discount-aware unit value, parallel to ReturnProduct.returnValue. When a
   * line item has a per-item or order-level discount, this is the post-discount
   * amount. Falls back to `price` when no discount applies. Used by the
   * customer portal to render the strikethrough/discounted price pair for
   * non-returnable items, mirroring eligible items.
   */
  returnValue: z.string().nullish(),
  currency: z.string(),
  quantity: z.number(),
  variantId: z.string().nullish(),
});
export const NonReturnableItemSchema: z.ZodType<NonReturnableItem> =
  NonReturnableItemSchema_;
export interface NonReturnableItem extends z.infer<
  typeof NonReturnableItemSchema_
> {}

const VariantExchangeItemSchema_ = z.object({
  variantId: z.string().nullish(),
  productId: z.string().nullish(),
  preserveProperties: z.boolean(),
  priceDifference: createEnum(["ignore", "apply"]).nullish(),
  title: z.string(),
  image: z.string().nullish(),
  productTitle: z.string().nullish(),
  portalDisplayMetafieldValues: z.array(z.string()).nullish(),
});
export const VariantExchangeItemSchema: z.ZodType<VariantExchangeItem> =
  VariantExchangeItemSchema_;
export interface VariantExchangeItem extends z.infer<
  typeof VariantExchangeItemSchema_
> {}

export const PendingReturnItemSchema_ = z.object({
  id: zExt.objectId(),
  returnableItemId: z.string(),
  expandedProductVariantExchanges: z.array(VariantExchangeItemSchema).nullish(),
  flow: z.any(),
  flowPath: z.array(z.number()).readonly(),
  locationFlow: z.any().nullish(),
  labelFlow: z.any().nullish(),
  multipleChoiceAnswers: z
    .array(z.object({ step: z.number(), answer: z.number() }))
    .readonly(),
  inputAnswers: z
    .array(
      z.object({
        type: createEnum(InputPartType),
        value: z.union([z.string(), z.array(z.string())]),
        message: z.string(),
        fieldId: createEnum(FieldId).nullish(),
        step: z.number().nullish(),
      }),
    )
    .readonly()
    .nullish(),

  exchangeGroup: exchangeGroupSchema.nullish(),
  variantExchangeItem: VariantExchangeItemSchema.nullish(),
  advancedExchangeItems: z.array(NewExchangeItemSchema).nullish(),
  manualReview: z.boolean(),
  manualReviewReason: z.string().nullish(),
  merchantReviewBeforeFiling: z.boolean().nullish(),
  flagged: z.boolean(),
  flaggedReason: z.string().nullish(),
  returnReason: z.string().nullish(),
  returnReasons: z.array(z.string()).default([]),
  rejectMessage: z.string().nullish(),
  rejectElementId: z.string().nullish(),
  reasonCode: z.string().nullish(),
  reasonCodes: z.array(z.string()).default([]),
  returnOptions: z.array(z.any()),
  flatRateShippingFee: z.string().nullish(),
  flatRateShippingFeeCurrencyOverrides: z
    .record(z.string(), z.number())
    .nullish(),
  flatRateShippingFeePerCurrencyAmountsEnabled: z.boolean().nullish(),
  dropoffFees: z
    .array(
      z.object({
        provider: z.enum(dropoffProviderType),
        fee: z.string(),
        currencyOverrides: z.record(z.string(), z.number()).nullish(),
        perCurrencyAmountsEnabled: z.boolean().nullish(),
      }),
    )
    .nullish(),
  /** The upcharge/downcharge value configured in the return flow */
  labelUpchargeValue: z.string().nullish(),
  /** Type of upcharge: "fixed" for fixed amount, "percentage" for percentage */
  labelUpchargeType: z.enum(["fixed", "percentage"]).nullish(),
  selectedReturnOption: z.any().nullish(),
  restockExternalLocationId: z.string().nullish(),
  quantity: z.number().nullish(),
  metadata: z.record(z.string(), z.any()).nullish(),
  notes: z.string().nullish(),
  merchantImages: z.array(z.string()).nullish(),
  aiDamaged: z.boolean().nullish(),
  aiWrongProduct: z.boolean().nullish(),
  aiReturnReasonsThreadId: zExt.objectId().nullish(),
  posInput: z.object({ restockRequested: z.boolean().nullish() }).nullish(),
  returnTags: z
    .array(z.object({ name: z.string(), color: z.string() }))
    .optional(),
  isCustomItem: z.boolean().nullish(),
});
export const PendingReturnItemSchema: z.ZodType<PendingReturnItem> =
  PendingReturnItemSchema_;
export interface PendingReturnItem extends z.infer<
  typeof PendingReturnItemSchema_
> {}

/**
 * For the frontend to pass an item to add to the draft return. Some fields will be fact checked and filled in on the server, rather than trusting the frontend.
 */
export const AddItemPendingItemSchema = PendingReturnItemSchema_.omit({
  id: true,
  manualReview: true,
  flagged: true,
  returnReason: true,
  returnOptions: true,
  selectedReturnOption: true,
  aiDamaged: true,
  aiWrongProduct: true,
  // Don't pass in the flow from the frontend. It will be set on the server.
  flow: true,
}).extend({ isBundleGroupReturn: z.boolean().nullish() });
export type AddItemPendingItem = z.infer<typeof AddItemPendingItemSchema>;

/** @param returnProductId - The id of the return_ product_, not the product ID (see get-returnable-items.ts) */
export function getReturnableItemId(
  returnProductId: string,
  returnableItemIndex: string,
) {
  return `${returnProductId}-${returnableItemIndex}`;
}

export function parseReturnableItemId(returnableItemId?: string) {
  if (!returnableItemId) {
    return undefined;
  }
  const separatorIndex = returnableItemId.lastIndexOf("-");
  if (separatorIndex === -1) {
    return {
      returnProductId: returnableItemId,
      returnableItemIndex: undefined,
    };
  }
  return {
    returnProductId: returnableItemId.slice(0, separatorIndex),
    returnableItemIndex: returnableItemId.slice(separatorIndex + 1),
  };
}

/** Merchant-readable reasons an item never reaches the return flow at all. */
export const NON_RETURNABLE_ITEM_REASONS = {
  EXCLUDED_TAG: "This product is tagged as excluded from returns",
  EXCLUDED_BY_SETTINGS: "This product is excluded from returns by settings",
  EXCLUDED_COLLECTION: "This product is in an excluded collection",
  NOT_FULFILLED: "This item has not been fulfilled",
  ALREADY_FULLY_RETURNED: "This item has already been fully returned",
  REJECTED: "This item has already been submitted for return and was rejected",
};
